import React from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import {
  ErrorOutline as ErrorIcon,
  WarningAmberOutlined as WarningIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { poolwerxColors } from '../../../theme/colors'
import type { AlertItem, AlertSeverity } from './types'

interface SeverityStyle {
  Icon: React.ComponentType<{ fontSize?: 'inherit' | 'large' | 'medium' | 'small'; sx?: object }>
  iconColor: string
  borderColor: string
  background: string
  actionVariant: 'contained' | 'outlined' | 'text'
  actionColor: 'error' | 'warning' | 'primary'
}

// Error reserved for failed / critical sync states - only treatment that
// keeps the contained red CTA. Warning gets an outlined amber button so a
// "data is stale" nudge doesn't feel as alarming as a real failure.
const SEVERITY_STYLES: Record<AlertSeverity, SeverityStyle> = {
  error: {
    Icon: ErrorIcon,
    iconColor: poolwerxColors.marketingRed,
    borderColor: poolwerxColors.marketingRed,
    background: poolwerxColors.errorSoft,
    actionVariant: 'contained',
    actionColor: 'error',
  },
  warning: {
    Icon: WarningIcon,
    iconColor: poolwerxColors.warning,
    borderColor: poolwerxColors.warning,
    background: poolwerxColors.warningSoft,
    actionVariant: 'outlined',
    actionColor: 'warning',
  },
  info: {
    Icon: InfoIcon,
    iconColor: poolwerxColors.teal,
    borderColor: poolwerxColors.teal,
    background: poolwerxColors.infoSoft,
    actionVariant: 'text',
    actionColor: 'primary',
  },
}

interface AlertRowProps {
  alert: AlertItem
}

const AlertRow: React.FC<AlertRowProps> = ({ alert }) => {
  const style = SEVERITY_STYLES[alert.severity]
  const { Icon } = style

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        py: 0.5,
        px: 2,
        borderRadius: '10px',
        borderLeft: `4px solid ${style.borderColor}`,
        border: '1px solid rgba(0,0,0,0.06)',
        borderLeftWidth: '4px',
        backgroundColor: style.background,
      }}
    >
      <Icon fontSize="small" sx={{ color: style.iconColor, flexShrink: 0 }} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.25,
          whiteSpace: { xs: 'nowrap', sm: 'normal' },
          overflow: { xs: 'hidden', sm: 'visible' },
          textOverflow: { xs: 'ellipsis', sm: 'clip' },
        }}
      >
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: poolwerxColors.charcoal,
            flexShrink: 0,
          }}
        >
          {alert.title}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.875rem',
            color: 'text.secondary',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: { xs: 'nowrap', sm: 'normal' },
          }}
        >
          {alert.message}
        </Typography>
      </Box>

      {alert.action && (
        <Button
          size="small"
          variant={style.actionVariant}
          color={style.actionColor}
          disableElevation
          disabled={alert.action.disabled}
          onClick={alert.action.onClick}
          sx={{
            textTransform: 'none',
            flexShrink: 0,
            minHeight: 28,
            py: 0.25,
            px: 1.25,
          }}
        >
          {alert.action.label}
        </Button>
      )}

      {alert.dismissible && (
        <IconButton
          size="small"
          onClick={alert.onDismiss}
          aria-label={`Dismiss ${alert.title}`}
          sx={{
            flexShrink: 0,
            opacity: 0.45,
            transition: 'opacity 120ms ease',
            '&:hover': { opacity: 1 },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  )
}

export default AlertRow
